import { InputColumnTableProps, InputTable, InputTextCustom, Modal } from 'medere-ui-framework'
import { Button } from 'primereact/button'
import { useEffect, useMemo, useState } from 'react'
import { HealthEntityPlanDTO, defaultHealthEntityPlanDTO } from '../../../dto/HealthEntityPlanDTO'
import { getCanManageExternalHealthEntityPlans, getExternalHealthEntityPlans } from '../../../services/ExternalHealthEntityPlanController'
import LocationInlineEditActions from '../../Location/components/LocationInlineEditActions'
import HealthEntityActionMenu, { HealthEntityActionMenuItem } from '../components/HealthEntityActionMenu'
import ExternalHealthEntityPlansDialog from '../components/ExternalHealthEntityPlansDialog'

function isPlanPersisted(item: HealthEntityPlanDTO) {
  return item.healthentityplan != null && item.healthentityplan > 0
}

interface HealthEntityPlansTabProps {
  plans: HealthEntityPlanDTO[]
  setPlans: React.Dispatch<React.SetStateAction<HealthEntityPlanDTO[]>>
  isMobile?: boolean
  readOnly?: boolean
}

export default function HealthEntityPlansTab({
  plans,
  setPlans,
  isMobile = false,
  readOnly = false,
}: Readonly<HealthEntityPlansTabProps>) {
  const [planItem, setPlanItem] = useState<HealthEntityPlanDTO>({
    ...defaultHealthEntityPlanDTO,
    healthentityplan: null,
    name: '',
    description: '',
    healthentity: null,
    healthentityname: '',
  })
  const [planErrors, setPlanErrors] = useState<{ name?: string }>({})
  const [editIndex, setEditIndex] = useState<number | null>(null)
  const [mobileModalVisible, setMobileModalVisible] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<HealthEntityPlanDTO | null>(null)
  const [externalPlansDialogVisible, setExternalPlansDialogVisible] = useState(false)
  const [externalPlanCounts, setExternalPlanCounts] = useState<Record<number, number>>({})
  const [canManageExternalPlans, setCanManageExternalPlans] = useState(false)

  const normalizedPlans = useMemo(() => plans ?? [], [plans])

  useEffect(() => {
    getCanManageExternalHealthEntityPlans()
      .then(setCanManageExternalPlans)
      .catch(() => setCanManageExternalPlans(false))
  }, [])

  const persistedPlanIds = useMemo(
    () => normalizedPlans
      .map(plan => plan.healthentityplan)
      .filter((id): id is number => id != null && id > 0),
    [normalizedPlans],
  )
  const persistedPlanIdsKey = persistedPlanIds.join(',')

  function refreshExternalPlanCount(healthEntityPlanId: number | null | undefined) {
    if (healthEntityPlanId == null || healthEntityPlanId <= 0) return
    getExternalHealthEntityPlans(healthEntityPlanId)
      .then(list => setExternalPlanCounts(prev => ({ ...prev, [healthEntityPlanId]: list.length })))
      .catch(() => { /* deja el conteo anterior si falla el refresco */ })
  }

  function handleExternalPlansDialogVisibleChange(visible: boolean) {
    setExternalPlansDialogVisible(visible)
    if (!visible) {
      refreshExternalPlanCount(selectedPlan?.healthentityplan)
    }
  }

  useEffect(() => {
    if (persistedPlanIds.length === 0) {
      setExternalPlanCounts({})
      return
    }

    let cancelled = false
    Promise.all(persistedPlanIds.map(id => getExternalHealthEntityPlans(id)
      .then(list => [id, list.length] as const)
      .catch(() => [id, 0] as const)))
      .then((results) => {
        if (cancelled) return
        setExternalPlanCounts(Object.fromEntries(results))
      })

    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistedPlanIdsKey])

  function clearEditor() {
    setPlanItem({
      ...defaultHealthEntityPlanDTO,
      healthentityplan: null,
      name: '',
      description: '',
      healthentity: null,
      healthentityname: '',
    })
    setPlanErrors({})
    setEditIndex(null)
  }

  function closeMobileModal() {
    setMobileModalVisible(false)
    clearEditor()
  }

  function hasDuplicateName(name: string, ignoredIndex: number | null = null) {
    const normalizedName = name.trim().toLowerCase()
    return normalizedPlans.some((plan, index) => index !== ignoredIndex
      && (plan.name ?? '').trim().toLowerCase() === normalizedName)
  }

  async function addOrUpdatePlan() {
    const errors: { name?: string } = {}
    if ((planItem.name ?? '').trim() === '') {
      errors.name = 'El nombre del plan es obligatorio'
    } else if (hasDuplicateName(planItem.name ?? '', editIndex)) {
      errors.name = 'El nombre del plan está repetido'
    }

    if (Object.keys(errors).length > 0) {
      setPlanErrors(errors)
      return false
    }

    const normalizedItem: HealthEntityPlanDTO = {
      ...planItem,
      name: (planItem.name ?? '').trim(),
      description: (planItem.description ?? '').trim(),
      healthentityplan: planItem.healthentityplan ?? null,
      healthentity: planItem.healthentity ?? null,
      healthentityname: planItem.healthentityname ?? '',
    }

    if (editIndex === null) {
      setPlans(prev => [...prev, normalizedItem])
    } else {
      setPlans(prev => prev.map((plan, index) => (index === editIndex ? normalizedItem : plan)))
    }
    clearEditor()
    if (isMobile) {
      setMobileModalVisible(false)
    }
    return true
  }

  function handleEdit(item: HealthEntityPlanDTO, index: number) {
    setPlanItem({
      ...item,
      healthentityplan: item.healthentityplan ?? null,
      name: item.name ?? '',
      description: item.description ?? '',
      healthentity: item.healthentity ?? null,
      healthentityname: item.healthentityname ?? '',
    })
    setPlanErrors({})
    setEditIndex(index)
  }

  function handleDelete(index: number) {
    setPlans(prev => prev.filter((_plan, i) => i !== index))
    if (editIndex === null) return
    if (editIndex === index) {
      clearEditor()
      return
    }
    if (index < editIndex) {
      setEditIndex(editIndex - 1)
    }
  }

  function openAddModal() {
    clearEditor()
    setMobileModalVisible(true)
  }

  function openEditModal(item: HealthEntityPlanDTO, index: number) {
    handleEdit(item, index)
    setMobileModalVisible(true)
  }

  function formatCardValue(value: string | null | undefined, emptyText = 'Sin datos') {
    const normalized = String(value ?? '').trim()
    return normalized !== '' ? normalized : emptyText
  }

  const columns: InputColumnTableProps<HealthEntityPlanDTO>[] = [
    {
      header: 'Nombre',
      className: 'w-5',
      body: (item) => item.name ?? '',
      input: (
        <InputTextCustom
          className={`${planErrors.name ? '' : 'border-grey'} w-full`}
          value={planItem.name ?? ''}
          onChange={(e) => {
            setPlanItem(prev => ({ ...prev, name: e.target.value }))
            setPlanErrors(prev => ({ ...prev, name: undefined }))
          }}
          error={!!planErrors.name}
          caption={planErrors.name}
        />
      ),
    },
    {
      header: 'Descripción',
      className: 'w-7',
      body: (item) => item.description ?? '',
      input: (
        <InputTextCustom
          className='border-grey w-full'
          value={planItem.description ?? ''}
          onChange={(e) => setPlanItem(prev => ({ ...prev, description: e.target.value }))}
        />
      ),
    },
    ...(canManageExternalPlans ? [{
      header: 'Planes externos vinculados',
      className: 'w-2 text-center',
      input: () => <div className='w-full'></div>,
      body: (item: HealthEntityPlanDTO) => {
        if (readOnly) return null
        const persisted = isPlanPersisted(item)
        if (!persisted) {
          return (
            <span
              className='health-entity-external-plans-link health-entity-external-plans-link--disabled'
              title='Guardá el plan antes de gestionar sus planes externos'
            >
              -
            </span>
          )
        }
        const count = externalPlanCounts[item.healthentityplan as number] ?? 0
        return (
          <button
            type='button'
            className='health-entity-external-plans-link'
            onClick={() => {
              setSelectedPlan(item)
              setExternalPlansDialogVisible(true)
            }}
            aria-label='Ver planes externos'
          >
            {count}
          </button>
        )
      },
    }] : []),
    {
      header: '',
      className: 'health-entity-inline-action-column',
      input: (
        <LocationInlineEditActions
          visible={!readOnly && editIndex !== null}
          onConfirm={() => { void addOrUpdatePlan() }}
          onCancel={clearEditor}
        />
      ),
      body: (item, index) => {
        if (readOnly) return null
        const menuItems: HealthEntityActionMenuItem[] = [
          ...(canManageExternalPlans ? [{
            label: 'Ver planes externos',
            icon: 'pi pi-list',
            action: () => {
              setSelectedPlan(item)
              setExternalPlansDialogVisible(true)
            },
            disabled: !isPlanPersisted(item),
          }] : []),
          {
            label: 'Editar',
            icon: 'pi pi-file-edit',
            action: () => handleEdit(item, index),
          },
          {
            label: 'Eliminar',
            icon: 'pi pi-trash',
            action: () => handleDelete(index),
          },
        ]

        return (
          <div className='flex justify-content-end'>
            <HealthEntityActionMenu
              menuItems={menuItems}
              buttonClassName='p-button-text'
              buttonAriaLabel='Opciones del plan'
              contentClassName='w-64'
            />
          </div>
        )
      },
    },
  ]

  if (!isMobile) {
    return (
      <div className='health-entity-table-tab'>
        <InputTable
          columnGap={4}
          iconAddBtn='pi pi-plus-circle text-3xl'
          headerDivClassName='font-semibold bg-bluegray-50'
          dividerClassName='my-1 border-divider'
          itemsClassName='health-entity-inputtable-item-card'
          buttonColumnWidth={43}
          items={normalizedPlans}
          columns={columns}
          onAdd={readOnly ? undefined : addOrUpdatePlan}
          divBtnClassName={!readOnly && editIndex !== null ? 'health-entity-inline-editing-hide-add' : ''}
          disabled={readOnly}
          divider
          dividerInFirstItem
        />
        <ExternalHealthEntityPlansDialog
          visible={externalPlansDialogVisible}
          setVisible={handleExternalPlansDialogVisibleChange}
          healthEntityPlanId={selectedPlan?.healthentityplan ?? null}
          healthEntityPlanName={selectedPlan?.name ?? undefined}
        />
      </div>
    )
  }

  return (
    <div className='health-entity-table-tab'>
      <div className='health-entity-mobile-section-header'>
        <h4 className='health-entity-section-title'>Planes</h4>
        {!readOnly && (
          <Button
            className='health-entity-mobile-add-btn'
            icon='pi pi-plus-circle'
            label='Agregar'
            onClick={openAddModal}
            aria-label='Agregar plan'
          />
        )}
      </div>

      <div className='health-entity-mobile-section-list'>
        {normalizedPlans.map((item, index) => {
          const menuItems: HealthEntityActionMenuItem[] = readOnly
            ? []
            : [
              ...(canManageExternalPlans ? [{
                label: 'Ver planes externos',
                icon: 'pi pi-list',
                action: () => {
                  setSelectedPlan(item)
                  setExternalPlansDialogVisible(true)
                },
                disabled: !isPlanPersisted(item),
              }] : []),
              {
                label: 'Editar',
                icon: 'pi pi-file-edit',
                action: () => openEditModal(item, index),
              },
              {
                label: 'Eliminar',
                icon: 'pi pi-trash',
                action: () => handleDelete(index),
              },
            ]

          return (
            <article
              key={`health-entity-plan-${item.healthentityplan ?? index}-${index}`}
              className='health-entity-mobile-record-card health-entity-mobile-note-plan-card'
            >
              <div className='health-entity-mobile-record-header'>
                <h4 className='health-entity-mobile-record-title'>{formatCardValue(item.name, 'Sin nombre')}</h4>
                {!readOnly && (
                  <HealthEntityActionMenu
                    menuItems={menuItems}
                    buttonClassName='p-button-text health-entity-mobile-record-menu-btn'
                    buttonAriaLabel='Opciones del plan'
                  />
                )}
              </div>

              <div className='health-entity-mobile-record-body'>
                <div className='health-entity-mobile-record-row'>
                  <span className='health-entity-mobile-record-key'>Descripción</span>
                  <span className='health-entity-mobile-record-value'>{formatCardValue(item.description)}</span>
                </div>
              </div>
            </article>
          )
        })}

        {normalizedPlans.length === 0 && (
          <p className='health-entity-mobile-empty'>No hay planes cargados.</p>
        )}
      </div>

      <Modal
        className='health-entity-mobile-editor-modal'
        visible={mobileModalVisible}
        setVisible={setMobileModalVisible}
        width='w-medium'
        onHideCustom={closeMobileModal}
        header={<h1 className='info-dark text-2xl'>{editIndex === null ? 'Agregar plan' : 'Editar plan'}</h1>}
        footerButtonLeftText='Cancelar'
        onClickLeftBtn={closeMobileModal}
        footerButtonRightText={editIndex === null ? 'Agregar' : 'Guardar'}
        onClickRightBtn={() => { void addOrUpdatePlan() }}
      >
        <div className='health-entity-mobile-modal-content'>
          <div className='health-entity-general-field'>
            <label className='block mb-2'>Nombre <span className='text-red-500'>*</span></label>
            <InputTextCustom
              className={`${planErrors.name ? '' : 'border-grey'} w-full`}
              value={planItem.name ?? ''}
              onChange={(e) => {
                setPlanItem(prev => ({ ...prev, name: e.target.value }))
                setPlanErrors(prev => ({ ...prev, name: undefined }))
              }}
              error={!!planErrors.name}
              caption={planErrors.name}
            />
          </div>

          <div className='health-entity-general-field'>
            <label className='block mb-2'>Descripción</label>
            <InputTextCustom
              className='border-grey w-full'
              value={planItem.description ?? ''}
              onChange={(e) => setPlanItem(prev => ({ ...prev, description: e.target.value }))}
            />
          </div>

        </div>
      </Modal>

      <ExternalHealthEntityPlansDialog
        visible={externalPlansDialogVisible}
        setVisible={handleExternalPlansDialogVisibleChange}
        healthEntityPlanId={selectedPlan?.healthentityplan ?? null}
        healthEntityPlanName={selectedPlan?.name ?? undefined}
      />
    </div>
  )
}
